import { LineSplitter } from "./codec";
import { cachedScreenTargets, listScreens, type ScreenTarget } from "./displays";
import { openLocalSurface, SurfaceBlockedError } from "./surface";

export type ScreenConnectionState =
  | "creating"
  | "waiting"
  | "connecting"
  | "connected"
  | "disconnected"
  | "failed"
  | "closed";

export interface ScreenPeerState {
  peerId: string;
  state: RTCPeerConnectionState;
}

export interface CastOptions {
  slug?: string;
  fps?: number;
  iceServers?: RTCIceServer[];
  onState?: (state: ScreenConnectionState, detail?: string) => void;
  onPeer?: (peer: ScreenPeerState) => void;
  onData?: (peerId: string, data: string | ArrayBuffer) => void;
}

export interface CastSession {
  readonly roomId: string;
  readonly receiverUrl: string;
  readonly stream: MediaStream;
  readonly peers: ReadonlyMap<string, RTCPeerConnection>;
  sendData(data: string | ArrayBuffer | ArrayBufferView): void;
  close(): Promise<void>;
}

export interface ReceiveOptions {
  iceServers?: RTCIceServer[];
  onState?: (state: ScreenConnectionState, detail?: string) => void;
  onStream: (stream: MediaStream) => void;
  onData?: (data: string | ArrayBuffer) => void;
}

export interface ReceiveSession {
  readonly roomId: string;
  readonly peerId: string;
  readonly peer: RTCPeerConnection;
  sendData(data: string | ArrayBuffer | ArrayBufferView): boolean;
  close(): Promise<void>;
}

interface RoomCredentials {
  roomId: string;
  peerId: string;
  peerToken: string;
  receiverUrl: string;
}

interface JoinCredentials {
  roomId: string;
  peerId: string;
  peerToken: string;
  senderPeerId: string;
}

interface SignalMessage {
  id: string;
  roomId: string;
  from: string;
  to: string;
  type: "peer-joined" | "peer-left" | "offer" | "answer" | "ice" | "renegotiate";
  payload?: unknown;
  ts: number;
}

interface SessionDescriptionPayload {
  type: RTCSdpType;
  sdp?: string;
}

const API_ROOT = "/api/playground/screens";

function inferPlaygroundSlug(): string {
  const parts = window.location.pathname.split("/").filter(Boolean);
  const playgroundIndex = parts.indexOf("playground");
  if (playgroundIndex < 0) throw new Error("Cannot infer playground slug from this page");
  const runtimeOffset = parts[playgroundIndex + 1] === "runtime" ? 2 : 1;
  const slug = parts[playgroundIndex + runtimeOffset];
  if (!slug || slug === "screen") throw new Error("Cannot infer playground slug from this page");
  return decodeURIComponent(slug);
}

async function jsonRequest<T>(url: string, init: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  const body = (await response.json().catch(() => null)) as { error?: string } | null;
  if (!response.ok) {
    throw new Error(body?.error ?? `Screen request failed (${response.status})`);
  }
  return body as T;
}

async function sendSignal(
  roomId: string,
  peerToken: string,
  to: string,
  type: "offer" | "answer" | "ice" | "renegotiate",
  payload?: unknown,
): Promise<void> {
  await jsonRequest(`${API_ROOT}/${encodeURIComponent(roomId)}/signal`, {
    method: "POST",
    headers: {
      authorization: `Bearer ${peerToken}`,
      "content-type": "application/json",
    },
    body: JSON.stringify({ to, type, payload }),
  });
}

function isSignalMessage(value: unknown): value is SignalMessage {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.id === "string" &&
    typeof record.from === "string" &&
    typeof record.to === "string" &&
    typeof record.type === "string"
  );
}

async function consumeSignalEvents(
  roomId: string,
  peerToken: string,
  signal: AbortSignal,
  onMessage: (message: SignalMessage) => void | Promise<void>,
): Promise<void> {
  while (!signal.aborted) {
    try {
      const response = await fetch(`${API_ROOT}/${encodeURIComponent(roomId)}/events`, {
        headers: { authorization: `Bearer ${peerToken}` },
        cache: "no-store",
        signal,
      });
      if (!response.ok || !response.body) {
        throw new Error(`Screen event stream failed (${response.status})`);
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (!signal.aborted) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
        let boundary: number;
        while ((boundary = buffer.indexOf("\n\n")) >= 0) {
          const block = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          const data = block
            .split("\n")
            .filter((line) => line.startsWith("data:"))
            .map((line) => line.slice(5).trimStart())
            .join("\n");
          if (!data) continue;
          const parsed: unknown = JSON.parse(data);
          if (isSignalMessage(parsed)) await onMessage(parsed);
        }
      }
    } catch (error) {
      if (signal.aborted) return;
      console.warn("[granrojo] screen signaling reconnect:", error);
    }
    await new Promise((resolve) => setTimeout(resolve, 750));
  }
}

function asSessionDescription(payload: unknown): RTCSessionDescriptionInit {
  if (typeof payload !== "object" || payload === null) {
    throw new Error("Invalid WebRTC session description");
  }
  const value = payload as Partial<SessionDescriptionPayload>;
  if (value.type !== "offer" && value.type !== "answer") {
    throw new Error("Invalid WebRTC session description type");
  }
  return { type: value.type, sdp: value.sdp };
}

function asIceCandidate(payload: unknown): RTCIceCandidateInit {
  if (typeof payload !== "object" || payload === null) {
    throw new Error("Invalid ICE candidate");
  }
  return payload as RTCIceCandidateInit;
}

function attachDataChannel(
  channel: RTCDataChannel,
  onData: ((data: string | ArrayBuffer) => void) | undefined,
): void {
  channel.binaryType = "arraybuffer";
  channel.onmessage = (event) => {
    if (typeof event.data === "string" || event.data instanceof ArrayBuffer) {
      onData?.(event.data);
    }
  };
}

function sendDataChannel(
  channel: RTCDataChannel,
  data: string | ArrayBuffer | ArrayBufferView,
): void {
  if (typeof data === "string") {
    channel.send(data);
  } else if (data instanceof ArrayBuffer) {
    channel.send(data);
  } else {
    // Copy ArrayBufferLike (including SharedArrayBuffer-backed views) into a
    // plain ArrayBuffer accepted by the browser RTCDataChannel overload.
    channel.send(Uint8Array.from(new Uint8Array(data.buffer, data.byteOffset, data.byteLength)));
  }
}

export async function cast(
  source: HTMLCanvasElement | MediaStream,
  options: CastOptions = {},
): Promise<CastSession> {
  options.onState?.("creating");
  const ownsStream = !(source instanceof MediaStream);
  const stream = ownsStream ? source.captureStream(options.fps ?? 30) : source;
  const slug = options.slug ?? inferPlaygroundSlug();
  const room = await jsonRequest<RoomCredentials>(API_ROOT, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ slug }),
  });
  const peers = new Map<string, RTCPeerConnection>();
  const channels = new Map<string, RTCDataChannel>();
  const pendingIce = new Map<string, RTCIceCandidateInit[]>();
  const abort = new AbortController();

  const closePeer = (peerId: string) => {
    channels.get(peerId)?.close();
    channels.delete(peerId);
    peers.get(peerId)?.close();
    peers.delete(peerId);
    pendingIce.delete(peerId);
  };

  const createPeer = async (peerId: string) => {
    if (peers.has(peerId)) return;
    const peer = new RTCPeerConnection({ iceServers: options.iceServers ?? [] });
    peers.set(peerId, peer);
    for (const track of stream.getTracks()) peer.addTrack(track, stream);
    const channel = peer.createDataChannel("granrojo-control", { ordered: true });
    channels.set(peerId, channel);
    attachDataChannel(channel, (data) => options.onData?.(peerId, data));
    peer.onicecandidate = (event) => {
      if (event.candidate) {
        void sendSignal(room.roomId, room.peerToken, peerId, "ice", event.candidate.toJSON());
      }
    };
    peer.onconnectionstatechange = () => {
      options.onPeer?.({ peerId, state: peer.connectionState });
      if (peer.connectionState === "connected") options.onState?.("connected");
      if (peer.connectionState === "failed") options.onState?.("failed", `Peer ${peerId} failed`);
    };
    const offer = await peer.createOffer();
    await peer.setLocalDescription(offer);
    await sendSignal(room.roomId, room.peerToken, peerId, "offer", peer.localDescription);
    options.onState?.("connecting", peerId);
  };

  const handleMessage = async (message: SignalMessage) => {
    if (message.type === "peer-joined") {
      const peerId = (message.payload as { peerId?: unknown } | undefined)?.peerId;
      if (typeof peerId === "string") await createPeer(peerId);
      return;
    }
    if (message.type === "peer-left") {
      const peerId = (message.payload as { peerId?: unknown } | undefined)?.peerId;
      if (typeof peerId === "string") closePeer(peerId);
      return;
    }
    const peer = peers.get(message.from);
    if (!peer) return;
    if (message.type === "answer") {
      await peer.setRemoteDescription(asSessionDescription(message.payload));
      for (const candidate of pendingIce.get(message.from) ?? []) {
        await peer.addIceCandidate(candidate);
      }
      pendingIce.delete(message.from);
    } else if (message.type === "ice") {
      const candidate = asIceCandidate(message.payload);
      if (peer.remoteDescription) await peer.addIceCandidate(candidate);
      else pendingIce.set(message.from, [...(pendingIce.get(message.from) ?? []), candidate]);
    } else if (message.type === "renegotiate") {
      await createPeer(message.from);
    }
  };

  void consumeSignalEvents(room.roomId, room.peerToken, abort.signal, handleMessage);
  options.onState?.("waiting", "Open the receiver link on a screen");

  return {
    roomId: room.roomId,
    receiverUrl: room.receiverUrl,
    stream,
    peers,
    sendData(data) {
      for (const channel of channels.values()) {
        if (channel.readyState === "open") sendDataChannel(channel, data);
      }
    },
    async close() {
      abort.abort();
      for (const peerId of [...peers.keys()]) closePeer(peerId);
      if (ownsStream) for (const track of stream.getTracks()) track.stop();
      await fetch(`${API_ROOT}/${encodeURIComponent(room.roomId)}`, {
        method: "DELETE",
        headers: { authorization: `Bearer ${room.peerToken}` },
        keepalive: true,
      }).catch(() => {});
      options.onState?.("closed");
    },
  };
}

export async function receive(
  roomId: string,
  joinToken: string,
  options: ReceiveOptions,
): Promise<ReceiveSession> {
  options.onState?.("creating");
  const joined = await jsonRequest<JoinCredentials>(
    `${API_ROOT}/${encodeURIComponent(roomId)}/join`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ joinToken }),
    },
  );
  const peer = new RTCPeerConnection({ iceServers: options.iceServers ?? [] });
  const abort = new AbortController();
  const pendingIce: RTCIceCandidateInit[] = [];
  let controlChannel: RTCDataChannel | null = null;
  const receivedStream = new MediaStream();

  peer.ontrack = (event) => {
    const stream = event.streams[0];
    if (stream) {
      options.onStream(stream);
      return;
    }
    receivedStream.addTrack(event.track);
    options.onStream(receivedStream);
  };
  peer.ondatachannel = (event) => {
    controlChannel = event.channel;
    attachDataChannel(event.channel, options.onData);
  };
  peer.onicecandidate = (event) => {
    if (event.candidate) {
      void sendSignal(
        roomId,
        joined.peerToken,
        joined.senderPeerId,
        "ice",
        event.candidate.toJSON(),
      );
    }
  };
  peer.onconnectionstatechange = () => {
    if (peer.connectionState === "connected") options.onState?.("connected");
    else if (peer.connectionState === "failed") options.onState?.("failed");
    else if (peer.connectionState === "disconnected") options.onState?.("disconnected");
    else options.onState?.("connecting");
  };

  const handleMessage = async (message: SignalMessage) => {
    if (message.from !== joined.senderPeerId) return;
    if (message.type === "offer") {
      await peer.setRemoteDescription(asSessionDescription(message.payload));
      for (const candidate of pendingIce.splice(0)) await peer.addIceCandidate(candidate);
      const answer = await peer.createAnswer();
      await peer.setLocalDescription(answer);
      await sendSignal(
        roomId,
        joined.peerToken,
        joined.senderPeerId,
        "answer",
        peer.localDescription,
      );
    } else if (message.type === "ice") {
      const candidate = asIceCandidate(message.payload);
      if (peer.remoteDescription) await peer.addIceCandidate(candidate);
      else pendingIce.push(candidate);
    }
  };

  void consumeSignalEvents(roomId, joined.peerToken, abort.signal, handleMessage);
  options.onState?.("waiting", "Waiting for sender");

  return {
    roomId,
    peerId: joined.peerId,
    peer,
    sendData(data) {
      if (controlChannel?.readyState !== "open") return false;
      sendDataChannel(controlChannel, data);
      return true;
    },
    async close() {
      abort.abort();
      controlChannel?.close();
      peer.close();
      await fetch(`${API_ROOT}/${encodeURIComponent(roomId)}`, {
        method: "DELETE",
        headers: { authorization: `Bearer ${joined.peerToken}` },
        keepalive: true,
      }).catch(() => {});
      options.onState?.("closed");
    },
  };
}

export interface CapturedFrame {
  bytes: Uint8Array;
  sequence: number;
  width: number;
  height: number;
  format: "jpeg" | "webp";
  capturedAt: number;
}

export interface FrameCaptureOptions {
  width?: number;
  height?: number;
  fps?: number;
  format?: "jpeg" | "webp";
  quality?: number;
}

export interface FrameCapture {
  stop(): void;
}

export function captureFrames(
  source: HTMLCanvasElement,
  options: FrameCaptureOptions,
  onFrame: (frame: CapturedFrame) => void | Promise<void>,
): FrameCapture {
  const width = options.width ?? source.width;
  const height = options.height ?? source.height;
  const fps = Math.max(1, Math.min(60, options.fps ?? 10));
  const format = options.format ?? "jpeg";
  const quality = Math.max(0, Math.min(1, options.quality ?? 0.78));
  const output = document.createElement("canvas");
  output.width = width;
  output.height = height;
  const context = output.getContext("2d");
  if (!context) throw new Error("Canvas 2D context is unavailable");
  let stopped = false;
  let busy = false;
  let sequence = 0;
  let lastAt = 0;
  let frameRequest = 0;

  const tick = (now: number) => {
    if (stopped) return;
    frameRequest = requestAnimationFrame(tick);
    if (busy || now - lastAt < 1000 / fps) return;
    lastAt = now;
    busy = true;
    context.drawImage(source, 0, 0, width, height);
    output.toBlob(
      (blob) => {
        if (!blob || stopped) {
          busy = false;
          return;
        }
        void blob
          .arrayBuffer()
          .then((buffer) =>
            onFrame({
              bytes: new Uint8Array(buffer),
              sequence: sequence++,
              width,
              height,
              format,
              capturedAt: Date.now(),
            }),
          )
          .catch((error) => console.error("[granrojo] frame capture failed:", error))
          .finally(() => {
            busy = false;
          });
      },
      `image/${format}`,
      quality,
    );
  };
  frameRequest = requestAnimationFrame(tick);
  return {
    stop() {
      stopped = true;
      cancelAnimationFrame(frameRequest);
    },
  };
}

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit++) {
      crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0);
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

export function encodeFramePacket(frame: CapturedFrame): Uint8Array {
  const headerSize = 18;
  const packet = new Uint8Array(headerSize + frame.bytes.byteLength + 4);
  packet.set([0x47, 0x52, 0x46, 0x31], 0); // GRF1
  const view = new DataView(packet.buffer);
  view.setUint8(4, 1);
  view.setUint8(5, frame.format === "jpeg" ? 1 : 2);
  view.setUint32(6, frame.sequence);
  view.setUint16(10, frame.width);
  view.setUint16(12, frame.height);
  view.setUint32(14, frame.bytes.byteLength);
  packet.set(frame.bytes, headerSize);
  view.setUint32(headerSize + frame.bytes.byteLength, crc32(packet.subarray(0, -4)));
  return packet;
}

export interface SerialFrameSink {
  write(frame: CapturedFrame): Promise<void>;
  close(): Promise<void>;
}

interface PendingFrameAck {
  sequence: number;
  resolve: () => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

export async function openSerialFrameSink(options: {
  baudRate?: number;
  filters?: SerialPortFilter[];
  ackTimeoutMs?: number;
} = {}): Promise<SerialFrameSink> {
  if (!("serial" in navigator)) throw new Error("WebSerial is unavailable in this browser");
  const port = await navigator.serial.requestPort(
    options.filters?.length ? { filters: options.filters } : undefined,
  );
  await port.open({ baudRate: options.baudRate ?? 921600 });
  const writer = port.writable?.getWriter();
  const reader = port.readable?.getReader();
  if (!writer || !reader) {
    await port.close();
    throw new Error("Serial port does not expose readable and writable streams");
  }
  const splitter = new LineSplitter();
  const decoder = new TextDecoder();
  let awaiting: PendingFrameAck | null = null;
  let closing = false;

  void (async () => {
    try {
      while (!closing) {
        const { value, done } = await reader.read();
        if (done) break;
        if (!value) continue;
        for (const line of splitter.push(decoder.decode(value, { stream: true }))) {
          const match = /^GRACK:(\d+)$/.exec(line.trim());
          const pending = awaiting as PendingFrameAck | null;
          if (!match || !pending || Number(match[1]) !== pending.sequence) continue;
          clearTimeout(pending.timer);
          const resolve = pending.resolve;
          awaiting = null;
          resolve();
        }
      }
    } catch (error) {
      const pending = awaiting as PendingFrameAck | null;
      pending?.reject(error instanceof Error ? error : new Error(String(error)));
      awaiting = null;
    }
  })();

  return {
    async write(frame) {
      if (closing) throw new Error("Serial frame sink is closed");
      if (awaiting) throw new Error("Previous frame is still awaiting acknowledgement");

      let resolveAck!: () => void;
      let rejectAck!: (error: Error) => void;
      const acknowledged = new Promise<void>((resolve, reject) => {
        resolveAck = resolve;
        rejectAck = reject;
      });
      const timer = setTimeout(() => {
        awaiting = null;
        rejectAck(new Error(`Frame ${frame.sequence} acknowledgement timed out`));
      }, options.ackTimeoutMs ?? 2000);
      const pending: PendingFrameAck = {
        sequence: frame.sequence,
        resolve: resolveAck,
        reject: rejectAck,
        timer,
      };
      awaiting = pending;

      try {
        await writer.write(encodeFramePacket(frame));
      } catch (error) {
        clearTimeout(pending.timer);
        awaiting = null;
        throw error;
      }
      await acknowledged;
    },
    async close() {
      closing = true;
      const pending = awaiting;
      if (pending) {
        clearTimeout(pending.timer);
        pending.reject(new Error("Serial frame sink closed"));
        awaiting = null;
      }
      await reader.cancel().catch(() => {});
      reader.releaseLock();
      writer.releaseLock();
      await port.close().catch(() => {});
    },
  };
}

export type PresentTransport = "local" | "cast";

export interface PresentOptions extends CastOptions {
  /** Physical display to place the surface on. Omit to leave it movable. */
  screenIndex?: number;
  /** Stable browsing-context name, so re-presenting reuses the same window. */
  windowName?: string;
  label?: string;
  /**
   * `local` (default) paints a same-origin window straight from this tab —
   * no room, no peer connection. `cast` forces the network path.
   */
  transport?: PresentTransport;
}

export interface PresentSession {
  readonly kind: PresentTransport;
  /** Only set on the network path — a local surface has no receiver link. */
  readonly receiverUrl?: string;
  close(): Promise<void>;
}

/**
 * A monitor on this machine. Local by default: the surface window is
 * same-origin, so pixels are copied in-process and this tab stays the only
 * JS context holding the canvas, the bus, and the hardware.
 */
export async function present(
  source: HTMLCanvasElement | MediaStream,
  options: PresentOptions = {},
): Promise<PresentSession> {
  if (options.transport !== "cast") {
    try {
      return await presentLocally(source, options);
    } catch (error) {
      // A blocked popup would block the cast receiver too, and an explicit
      // `local` request should not silently reach the network.
      if (error instanceof SurfaceBlockedError || options.transport === "local") throw error;
      console.warn("[granrojo] local display unavailable, casting instead:", error);
    }
  }
  return presentOverCast(source, options);
}

async function presentLocally(
  source: HTMLCanvasElement | MediaStream,
  options: PresentOptions,
): Promise<PresentSession> {
  // Resolve the target *before* window.open. getScreenDetails() does not
  // consume the user gesture, so the open() features can include left/top
  // on the chosen monitor. Opening first and moveTo later is ignored on macOS.
  let placement: ScreenTarget | undefined;
  if (options.screenIndex !== undefined) {
    const targets = cachedScreenTargets() ?? (await listScreens());
    placement = targets[options.screenIndex];
  }

  const surface = openLocalSurface(source, {
    label: options.label,
    windowName: options.windowName,
    fps: options.fps,
    placement,
    onClose: () => options.onState?.("closed"),
  });
  if (placement) surface.place(placement);

  options.onState?.("connected", "Local display");
  return {
    kind: "local",
    close: () => surface.close(),
  };
}

async function presentOverCast(
  source: HTMLCanvasElement | MediaStream,
  options: PresentOptions,
): Promise<PresentSession> {
  const popup = window.open(
    "about:blank",
    options.windowName ?? `granrojo-screen-${Date.now().toString(36)}`,
  );
  if (!popup) throw new SurfaceBlockedError();
  popup.document.title = "Connecting Granrojo screen…";
  popup.document.body.textContent = "Connecting screen…";
  try {
    // Only place the window when a display was chosen — without an index the
    // popup stays where the browser put it, movable by hand.
    const target =
      options.screenIndex === undefined
        ? undefined
        : (cachedScreenTargets() ?? (await listScreens()))[options.screenIndex];
    if (target) {
      popup.moveTo(target.left, target.top);
      popup.resizeTo(target.width, target.height);
    }
    const session = await cast(source, options);
    popup.location.replace(session.receiverUrl);
    return {
      kind: "cast",
      receiverUrl: session.receiverUrl,
      close: () => session.close(),
    };
  } catch (error) {
    popup.close();
    throw error;
  }
}

export const screens = {
  cast,
  receive,
  present,
  captureFrames,
  encodeFramePacket,
  openSerialFrameSink,
};
